import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  attemptShowErrorsInSnackbar,
  useSnackbarActionContext,
} from "../context/SnackbarContext";
import UsersService from "@velocity-ehs/user-utils-module";
import {
  CreateEhsRoleInput,
  EhsPermission,
  EhsQualifier,
  EhsRoleFormFields,
  UpdateEhsRoleInput,
} from "../components/routeComponents/EhsConfiguration/EhsRoleTypes";
import EhsService from "../services/EhsService";
import { SnackbarType } from "../components/sharedComponents/Snackbar";

export enum EhsRoleActions {
  CREATE = "create",
  UPDATE = "update",
}

const useEhsRoleMutation = () => {
  const create = useMutation(
    (req: CreateEhsRoleInput) => EhsService.createEhsRole(req),
    ResponseHandling("ehs_role_create_success")
  );
  const update = useMutation(
    (req: UpdateEhsRoleInput) => EhsService.updateEhsRole(req),
    ResponseHandling("ehs_role_update_success")
  );

  const mutate = async (
    action: EhsRoleActions,
    input: EhsRoleFormFields,
    roleId: string,
    activePermissions: EhsPermission[]
  ) => {
    switch (action) {
      case EhsRoleActions.CREATE:
        const createEhsRole = mapFormToCreate(input, activePermissions);
        return await create.mutateAsync(createEhsRole);
      case EhsRoleActions.UPDATE:
        const updateEhsRole = mapFormToUpdate(roleId, input, activePermissions);
        return await update.mutateAsync(updateEhsRole);
      default:
        throw new Error(`Invalid action: ${action}`);
    }
  };

  return {
    mutations: {
      mutate,
      isLoading: create.isLoading || update.isLoading,
      create: {
        isSuccess: create.isSuccess,
        createEhsRoleId: create.data?.id,
      },
    },
  };
};

export default useEhsRoleMutation;

function mapFormToCreate(
  input: EhsRoleFormFields,
  activePermissions: EhsPermission[]
): CreateEhsRoleInput {
  return {
    customerId: UsersService.customer.id,
    name: input.name,
    description: input.description,
    activePermissionMappings: mapActivePermissionsToForm(activePermissions),
  };
}

function mapFormToUpdate(
  roleId: string,
  input: EhsRoleFormFields,
  activePermissions: EhsPermission[]
): UpdateEhsRoleInput {
  return {
    id: roleId,
    customerId: UsersService.customer.id,
    name: input.name,
    description: input.description,
    activePermissionMappings: mapActivePermissionsToForm(activePermissions),
  };
}

function mapQualifiers(qualifiers: Array<EhsQualifier>): Array<string> {
  return qualifiers.map((qualifier) => qualifier.id);
}

function mapActivePermissionsToForm(permissions: Array<EhsPermission>): Array<{
  permissionId: string;
  qualifierIds: Array<string>;
}> {
  return permissions.map((permission) => ({
    permissionId: permission.id,
    qualifierIds: mapQualifiers(permission.assignedQualifiers),
  }));
}

function ResponseHandling(messageKey: string = "ehs_role_update_success") {
  const { showSnackbar } = useSnackbarActionContext();
  const { t } = useTranslation();

  return {
    onError: (error) => {
      attemptShowErrorsInSnackbar(showSnackbar, error);
    },
    onSuccess: () => {
      showSnackbar(SnackbarType.Success, t(messageKey));
    },
  };
}
